import React, { useEffect, useState } from "react"
import { Edit as EditIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useRouteMatch, useHistory } from "react-router-dom"
import { Button, Tabs } from "antd"
import { Elements } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"

import Loading from "Components/loading"
import { FINWAY_ADMIN_EMAIL, STRIPE_API_KEY } from "Shared/config/consts"
import { ThunkDispatchResult } from "Shared/store"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import { fetchPaymentSettings, updatePaymentSettings } from "Shared/store/actions/payment/paymentActions"
import { usePaymentSettings } from "Shared/hooks/payment.hooks"
import { EnrichedPaymentSettings } from "@finway-group/shared/lib/models"
import { paymentSettingsLink, SettingsRouteMatchInterface } from "../settings.container"
import { isDev, isInternal } from "Shared/utils/helpers"
import { useLoggedInEmployeeProfile } from "Shared/hooks/user.hooks"

const stripePromise = loadStripe(STRIPE_API_KEY)
const { TabPane } = Tabs

const PaymentSubscriptionSettings = React.lazy(() => import("./paymentSubscriptionSettings"))
const PaymentMethodSettings = React.lazy(() => import("./paymentMethodSettings"))
const PaymentInvoices = React.lazy(() => import("./paymentInvoice.list"))

export enum PaymentSettingsTabEnum {
    SUBSCRIPTION = "plan",
    PAYMENT_METHODS = "payment_method",
    INVOICES = "invoices",
}

// options applied to all stripe elements
const elementsOptions: any = {
    appearance: { variables: { fontFamily: "proxima-nova, sans-serif", fontSize: "15px" } },
}

const PaymentSettingsContainer = () => {
    const history = useHistory()
    const {
        params: { settingsTab },
    } = useRouteMatch<SettingsRouteMatchInterface>()
    const dispatch: ThunkDispatchResult = useDispatch()
    const { t } = useTranslation()
    const [activeTab, setActiveTabBasic] = useState(PaymentSettingsTabEnum.SUBSCRIPTION)
    const [isPaymentMethodFormEditable, setIsPaymentMethodFormEditable] = useStateIfMounted(false)
    const paymentSettings = usePaymentSettings()
    const loggedInUser = useLoggedInEmployeeProfile()

    useEffect(() => {
        dispatch(fetchPaymentSettings())
    }, [dispatch])

    useEffect(() => {
        if (settingsTab) setActiveTab(settingsTab as PaymentSettingsTabEnum, false)
    }, [settingsTab])

    const handleEdit = () => {
        if (activeTab === PaymentSettingsTabEnum.PAYMENT_METHODS) {
            setIsPaymentMethodFormEditable(true)
        }
    }

    const onUpdated = (update: EnrichedPaymentSettings) => dispatch(updatePaymentSettings(update))

    const setActiveTab = (activeTab: PaymentSettingsTabEnum, isPushHistory = true) => {
        if (isPushHistory) history.push(`/settings/${paymentSettingsLink}/${activeTab}`)
        setActiveTabBasic(activeTab)
    }

    if (paymentSettings.isFetching || !paymentSettings.data) return <Loading />

    const tabs = [
        {
            title: t("label:payment_settings.plan"),
            key: PaymentSettingsTabEnum.SUBSCRIPTION,
            component: <PaymentSubscriptionSettings paymentSettings={paymentSettings.data} onUpdated={onUpdated} setActiveTab={setActiveTab} />,
        },
        {
            key: PaymentSettingsTabEnum.PAYMENT_METHODS,
            title: t("label:payment_settings.payment"),
            component: (
                <Elements stripe={stripePromise} options={elementsOptions}>
                    <PaymentMethodSettings
                        paymentSettings={paymentSettings.data}
                        onUpdated={onUpdated}
                        isFormEditable={isPaymentMethodFormEditable}
                        setIsFormEditable={setIsPaymentMethodFormEditable}
                    />
                </Elements>
            ),
        },
        {
            key: PaymentSettingsTabEnum.INVOICES,
            title: t("label:payment_settings.invoices"),
            component: <PaymentInvoices activeTab={activeTab} />,
        },
    ]

    const renderButtons = () => {
        if (activeTab === PaymentSettingsTabEnum.PAYMENT_METHODS) {
            if (paymentSettings.data?.paymentMethod) {
                return (
                    <Button onClick={handleEdit}>
                        <EditIcon />
                        <span>{t("action:edit")}</span>
                    </Button>
                )
            }
            return <></>
        }
        return <></>
        // TODO: enable when implemented
        // <Button>
        //     <span>{t("action:payment_settings.contact_support")}</span>
        // </Button>
    }

    const renderTestButton = () => {
        const isTest = isDev || isInternal
        return (
            <>
                {(isDev || isInternal || loggedInUser.email === FINWAY_ADMIN_EMAIL) && paymentSettings.data?.customerId && (
                    <a target="_blank" href={`https://dashboard.stripe.com/${isTest ? "test/" : ""}customers/${paymentSettings.data?.customerId}`}>
                        <Button>View Customer On Stripe</Button>
                    </a>
                )}
            </>
        )
    }

    return (
        <div className="pr-20 pl-20">
            <div className="justify-between items-center mb-10 hidden md:flex">
                <h2 className="mb-0">{t("label:payment_settings.title")}</h2>
                {renderButtons()}
            </div>
            <Tabs activeKey={activeTab} onChange={(key: PaymentSettingsTabEnum) => setActiveTab(key)}>
                {tabs.map((tab) => (
                    <TabPane tab={tab.title} key={tab.key} className="pb-10">
                        {tab.component}
                    </TabPane>
                ))}
            </Tabs>
            {renderTestButton()}
        </div>
    )
}

export default React.memo(PaymentSettingsContainer)
